import isEmpty from 'lodash/isEmpty';
import size from 'lodash/size';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useAppSelector } from '../../app/redux/hooks';
import { FieldData } from '../../app/user/profile/profileUtils';
import DependencyField from './DependencyField';

/**
 * Generate list of form components for a field group
 * @param {string[]} sortedFields ordered list of field names
 * @param {UseFormReturn} methods form methods
 * @param {FieldData} userData users field data
 * @param {string} group name of group to generate
 * @param {boolean} userAccessRestriction where to restrict on userAccess value
 * @param {boolean} booleanAsCheckbox whether to display booleans as a checkbox
 * @param {boolean} displayLabel whether to display booleans as a checkbox
 * @return {JSX[]}
 */
export const generateFormComponents = (
  sortedFields: any,
  methods: UseFormReturn,
  userData: FieldData,
  group: string,
  userAccessRestriction?: boolean,
  booleanAsCheckbox?: boolean,
  displayLabel?: boolean
) => {
  const profileDetails = useAppSelector((state) => state.profile?.profile ?? null);
  if (isEmpty(profileDetails)) return null;
  const fields = profileDetails.fieldGroups[group].fields;

  return sortedFields.map((fieldName: string, i: number) => {
    // don't display if dependent on another field, handling in component, nor display on restriction
    if (
      size(fields[fieldName]?.fieldDependees) > 0 ||
      (userAccessRestriction && !(fields[fieldName] as any).userAccessField)
    ) {
      return;
    }

    // files submit on change so need the group info to pass into the field but avoid state mutation
    const field = { ...fields[fieldName] };
    if (fields[fieldName]?.type == 'File') {
      field.fieldGroup = group;
    }

    // dependency field will render the provided field AND dependents
    return (
      <div key={fieldName + '+' + i + '_div'}>
        <DependencyField
          methods={methods}
          key={fieldName + '+' + i}
          field={field}
          userData={userData}
          booleanAsCheckbox={booleanAsCheckbox}
          displayLabel={displayLabel}
        />
      </div>
    );
  });
};

/**
 * Generate flat key - value object list generated from profile FieldData obj
 * @param {FieldData} userData
 * @param {string[]} sortedFields
 * @return {Object}
 */
export const generateDefaultValues = (userData: FieldData, sortedFields: any) => {
  // form components are keyed flatly so ensure default values are as well
  const defaultValues: any = {};
  Object.keys(userData).map((fieldValueKey: string) => {
    if (!sortedFields.includes(fieldValueKey)) {
      return;
    }
    const fieldValue = userData[fieldValueKey];
    if (Array.isArray(fieldValue.rawValue)) {
      const fieldValueArray: any = [];
      fieldValue.rawValue.map((rawValue: any) => fieldValueArray.push(rawValue.value));
      return (defaultValues[fieldValueKey] = fieldValueArray);
    }
    // check whether there is some form of value, boolean value check is fine as rawValue will be string
    return (defaultValues[fieldValueKey] = fieldValue.value || fieldValue.rawValue ? fieldValue.rawValue : null);
  });

  return defaultValues;
};
